'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, UIEvent } from 'react'

import { cx } from '@/app/lib/utils'

import { isDateInRange, isInRange, isRangeEndpoint, isSameDay, stripTime, toIsoDate } from '@/app/components/shared/date/Calendar/utils/dateRange'
import styles from './DayStrip.module.scss'
import type { DayStripProps } from './DayStrip.types'

/** Nº de días que muestra la tira por defecto — el resto del rango permitido
 * solo es alcanzable a través de un calendario completo (p. ej. `DatePicker`). */
const DEFAULT_STRIP_DAYS = 90

const WEEKDAY_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
const MONTH_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function addDays(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount)
}

function buildStripDays(min: Date, max: Date, count: number): Date[] {
  const days: Date[] = []
  for (let i = 0; i < count; i += 1) {
    const date = addDays(min, i)
    if (!isDateInRange(date, min, max)) break
    days.push(date)
  }
  return days
}

interface DayCell {
  readonly date: Date
  /** Solo poblado en el primer día de cada mes visible en la tira — con 90
   * días la tira cubre casi siempre 3-4 meses, así que esta fila puede mostrar
   * varias etiquetas ("sep 2026", "oct 2026"…) a la vez, una por cada mes que
   * entra en la ventana visible. */
  readonly monthLabel: string | null
}

function buildDayCells(days: Date[]): DayCell[] {
  return days.map((date, index) => {
    const previous = days[index - 1]
    const isFirstOfMonth = index === 0 || date.getMonth() !== previous?.getMonth() || date.getFullYear() !== previous?.getFullYear()
    return { date, monthLabel: isFirstOfMonth ? `${MONTH_SHORT[date.getMonth()]} ${date.getFullYear()}` : null }
  })
}

/**
 * `DayStrip` — tira horizontal de accesos rápidos a los próximos días dentro de
 * `[min, max]` (`days` de ellos, como máximo — 90 por defecto), con una fila de
 * etiquetas de mes alineada célula a célula sobre ella (fiel al diseño de
 * referencia: `monthLabelsRow`/`monthLabelCell` con el mismo ancho que cada
 * botón de día, no una etiqueta flotante por encima) — al desplazarse por la
 * tira y cruzar un cambio de mes, la etiqueta del mes nuevo aparece sola en su
 * propia célula (p. ej. "sep 2026" → "oct 2026"), sin lógica adicional: ya es
 * el comportamiento natural de una etiqueta por día. Selección de una única
 * fecha o de un rango de dos (`DateRange`, ver `dateRange.ts`) — el propio
 * componente no decide qué click hace qué; eso lo resuelve quien lo compone
 * (`DatePicker`) vía `onSelect`. Componente compartido y genérico (Atomic
 * Design `shared/` — Principio III): no conoce ninguna página ni regla de
 * negocio concreta.
 */
export function DayStrip({
  range,
  min,
  max,
  onSelect,
  hasSelection = true,
  scrollToStart = false,
  days = DEFAULT_STRIP_DAYS,
  variant = 'default',
  className,
}: DayStripProps) {
  const cells = useMemo(() => buildDayCells(buildStripDays(min, max, days)), [min, max, days])
  const today = useMemo(() => stripTime(new Date()), [])

  const scrollerRef = useRef<HTMLDivElement>(null)
  const monthLabelsRef = useRef<HTMLDivElement>(null)

  // Extremo "activo" del rango a la hora de decidir a qué desplazar la vista —
  // el fin si ya hay un rango cerrado, el inicio si es una única fecha (nunca
  // `null`: `DateRange.start` siempre existe).
  const activeIso = toIsoDate(scrollToStart ? range.start : (range.end ?? range.start))

  // Desplaza el extremo activo del rango a la vista cuando cambia (p. ej. al
  // pulsar una flecha de avance/retroceso repetidamente hasta salir de la
  // ventana visible inicial — specs/042-modulo-agenda-cms, research.md §8, y
  // al cerrar un rango con un segundo click fuera de la ventana visible
  // inicial): sin esto, el visitante perdería la referencia visual de qué día
  // está viendo aunque el listado sí se actualice. `prefers-reduced-motion`
  // desactiva la animación (Principio VII).
  //
  // Se calcula y aplica el scroll a mano sobre el propio `scroller` (mismo
  // patrón que `MultimediaGallery.tsx`), en vez de `target.scrollIntoView()`:
  // ese método nativo recorre TODOS los contenedores de scroll ascendentes,
  // incluida la página entera — con `block: 'nearest'`, si esta tira se monta
  // por debajo del pliegue (p. ej. el módulo Agenda en Home), el navegador
  // puede decidir que el elemento "no está visible" verticalmente y hacer
  // scroll de TODA la página hacia abajo, no solo de esta tira horizontal.
  // Acotarlo al propio `scroller` garantiza que nunca toque el scroll
  // vertical de la página, sin importar en qué momento se monte.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const target = scroller.querySelector<HTMLElement>(`[data-iso="${activeIso}"]`)
    if (!target) return
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // Con `scrollToStart` el primer día del rango queda como primera celda
    // visible (borde izquierdo), para que el resto del rango se vea a su
    // derecha; si no, el extremo activo se centra.
    const left = scrollToStart
      ? target.offsetLeft - scroller.offsetLeft
      : target.offsetLeft + target.offsetWidth / 2 - scroller.clientWidth / 2
    scroller.scrollTo({ left, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
    // Sincroniza de inmediato la fila de meses con el destino del scroll — no
    // basta con esperar al evento `scroll` de abajo: con `behavior: 'auto'`
    // (o el mock de `scrollTo` en jsdom) el desplazamiento puede no disparar
    // ningún evento `scroll` en absoluto.
    monthLabelsRef.current?.scrollTo({ left, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
  }, [activeIso, scrollToStart])

  // La fila de etiquetas de mes (`monthLabelsRow`) es un elemento hermano de
  // `scroller`, no un hijo suyo — sin este listener, su `scrollLeft` se queda
  // fijo en 0 para siempre y solo muestra las etiquetas de los primeros días
  // visibles al montar (p. ej. siempre "sep 2026"/"oct 2026"), por más que el
  // visitante desplace la tira de días hacia meses posteriores. Al reflejar
  // aquí el `scrollLeft` real del scroller, la fila de meses se mueve al
  // mismo ritmo — misma anchura de célula por día en ambas filas (44px +
  // mismo gap), así que quedan alineadas sin cálculo adicional.
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const monthLabels = monthLabelsRef.current
    if (monthLabels) monthLabels.scrollLeft = event.currentTarget.scrollLeft
  }

  // Roving tabindex: la tira entera es UNA sola parada de Tab (el día que
  // tuvo el foco por última vez, o si no el extremo activo del rango, o el
  // primer día), de modo que Tab salta de la tira al siguiente control
  // (selector de calendario) en vez de recorrer hasta 90 botones. Dentro de la
  // tira se navega con las flechas izquierda/derecha (+ Inicio/Fin).
  const [focusedIso, setFocusedIso] = useState<string | null>(null)
  // Al cambiar la selección (fecha única o rango, venga de la tira, del
  // calendario, de las flechas o de un filtro externo) la parada de Tab vuelve
  // a la primera fecha seleccionada, en vez de quedarse en el último día al que
  // se llegó navegando con las flechas. Ajuste de estado durante el render (no
  // efecto) para no pintar un fotograma con la parada antigua.
  const selectionKey = `${toIsoDate(range.start)}|${range.end ? toIsoDate(range.end) : ''}`
  const [lastSelectionKey, setLastSelectionKey] = useState(selectionKey)
  if (selectionKey !== lastSelectionKey) {
    setLastSelectionKey(selectionKey)
    setFocusedIso(null)
  }
  const startIso = toIsoDate(range.start)
  const hasCell = (iso: string | null): iso is string => iso !== null && cells.some(({ date }) => toIsoDate(date) === iso)
  const tabStopIso = hasCell(focusedIso) ? focusedIso : hasCell(startIso) ? startIso : cells[0] ? toIsoDate(cells[0].date) : null

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { key } = event
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-iso]'))
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (current === -1) return
    event.preventDefault()
    let next = current
    if (key === 'ArrowLeft') next = Math.max(0, current - 1)
    else if (key === 'ArrowRight') next = Math.min(buttons.length - 1, current + 1)
    else if (key === 'Home') next = 0
    else next = buttons.length - 1
    buttons[next]?.focus()
  }

  return (
    <div className={cx(styles.root, variant === 'inverse' && styles.inverse, className)}>
      <div ref={monthLabelsRef} className={styles.monthLabelsRow} aria-hidden="true">
        {cells.map(({ date, monthLabel }) => (
          <div key={toIsoDate(date)} className={styles.monthLabelCell}>
            {monthLabel && <span className={styles.monthLabel}>{monthLabel}</span>}
          </div>
        ))}
      </div>

      <div
        ref={scrollerRef}
        className={styles.scroller}
        role="group"
        aria-label="Seleccionar fecha"
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
      >
        {cells.map(({ date }) => {
          const isEndpoint = hasSelection && isRangeEndpoint(date, range)
          const inRange = hasSelection && isInRange(date, range)
          const isToday = isSameDay(date, today)

          return (
            <button
              key={toIsoDate(date)}
              type="button"
              data-iso={toIsoDate(date)}
              tabIndex={toIsoDate(date) === tabStopIso ? 0 : -1}
              onFocus={() => setFocusedIso(toIsoDate(date))}
              aria-pressed={isEndpoint}
              aria-label={date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              className={cx(
                styles.day,
                isEndpoint && styles.daySelected,
                inRange && styles.dayInRange,
                isToday && !isEndpoint && styles.dayToday,
              )}
              onClick={() => onSelect(date)}
            >
              <span className={styles.dayName} aria-hidden="true">
                {WEEKDAY_SHORT[date.getDay()]}
              </span>
              <span className={styles.dayNum} aria-hidden="true">
                {date.getDate()}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
